import { useEffect, useMemo, useRef, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/Stack'

import { getOrgVenues, getSalesVelocityVenueHeatmap } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { ReportRefreshButton } from '../ReportRefreshButton'
import SalesVelocityHeatmapMap from './SalesVelocityHeatmapMap'
import SalesVelocityMetricsGuideModal from './SalesVelocityMetricsGuideModal'
import { HEATMAP_METRICS } from './salesVelocityHeatmapUtils'
import './salesVelocityWrapper.scss'

const TUTORIAL_VIDEO_SOURCE =
  'https://blocktickets.nyc3.cdn.digitaloceanspaces.com/tutorials/Seat_Velocity.mp4'

function InsightList({ title, items, renderItem, emptyMessage }) {
  return (
    <div className="sales-velocity-insight-block">
      <h3 className="sales-velocity-insight-title">{title}</h3>
      {items?.length ? (
        <ul className="sales-velocity-insight-list">
          {items.map((item, index) => (
            <li key={index}>{renderItem(item)}</li>
          ))}
        </ul>
      ) : (
        <p className="text-muted small mb-0">{emptyMessage}</p>
      )}
    </div>
  )
}

export default function SalesVelocityWrapper({ organization }) {
  const [venues, setVenues] = useState([])
  const [venueUUID, setVenueUUID] = useState('')
  const [metricId, setMetricId] = useState('sell_through')
  const [showMetricsGuide, setShowMetricsGuide] = useState(false)
  const [heatmapData, setHeatmapData] = useState(null)
  const [isLoadingVenues, setIsLoadingVenues] = useState(true)
  const [isLoadingHeatmap, setIsLoadingHeatmap] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingHeatmap)
  const [loadError, setLoadError] = useState(null)

  const organizationUUID = organization?.uuid
  const selectedMetric = HEATMAP_METRICS.find((m) => m.id === metricId)

  const [showTutorial, setShowTutorial] = useState(false)
  const tutorialVideoRef = useRef(null)

  const handleCloseTutorial = () => {
    if (tutorialVideoRef.current) {
      tutorialVideoRef.current.pause()
      tutorialVideoRef.current.currentTime = 0
    }
    setShowTutorial(false)
  }

  useEffect(() => {
    if (!organizationUUID) return

    let cancelled = false
    setIsLoadingVenues(true)

    getOrgVenues(organizationUUID)
      .then((res) => {
        if (cancelled) return
        const list = Array.isArray(res?.data) ? res.data : []
        setVenues(list)
        if (list.length === 1) {
          setVenueUUID(list[0].uuid)
        }
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load venues', err)
        setLoadError('Could not load venues for this organization.')
      })
      .finally(() => {
        if (!cancelled) setIsLoadingVenues(false)
      })

    return () => {
      cancelled = true
    }
  }, [organizationUUID])

  useEffect(() => {
    if (!organizationUUID || !venueUUID) {
      setHeatmapData(null)
      return
    }

    let cancelled = false
    setIsLoadingHeatmap(true)
    setLoadError(null)

    getSalesVelocityVenueHeatmap(organizationUUID, venueUUID)
      .then((res) => {
        if (cancelled) return
        setHeatmapData(res?.data ?? null)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load sales velocity heatmap', err)
        setLoadError('Could not load seat location analytics for this venue.')
        setHeatmapData(null)
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingHeatmap(false)
          completeRefresh()
        }
      })

    return () => {
      cancelled = true
    }
  }, [organizationUUID, venueUUID, refreshToken])

  const insights = heatmapData?.insights
  const isLoading = isLoadingVenues || isLoadingHeatmap

  const venueOptions = useMemo(
    () =>
      venues.map((v) => (
        <option key={v.uuid} value={v.uuid}>
          {v.name}
        </option>
      )),
    [venues]
  )

  const metricOptions = useMemo(
    () =>
      HEATMAP_METRICS.map((m) => (
        <option key={m.id} value={m.id}>
          {m.label}
        </option>
      )),
    []
  )

  const renderEmptyState = () => {
    if (heatmapData?.error === 'seatmap_not_found') {
      return (
        <Card body className="card--xs card-with-border">
          <p className="text-muted mb-0">
            No seatmap is configured for{' '}
            {heatmapData?.venue?.name || 'this venue'}. Add a seatmap in venue
            settings to use the heatmap view.
          </p>
        </Card>
      )
    }

    if (heatmapData && heatmapData.seatCount === 0) {
      return (
        <Card body className="card--xs card-with-border">
          <p className="text-muted mb-0">
            No seat location analytics yet for this venue. Analytics are built
            as events complete; run the seat-location analytics backfill if you
            need historical data.
          </p>
        </Card>
      )
    }

    return null
  }

  return (
    <section
      className="spacer-sm sales-velocity-wrapper"
      id="sales-velocity-wrapper"
    >
      <header className="section-header-sm report-page-header">
        <div className="section-heading">
          <h1>Sales Velocity</h1>
        </div>
        <p className="section-header-desc">
          Venue heatmap of historical sell-through and demand for{' '}
          {organization?.name || 'your organization'}. Warmer colors indicate
          stronger performance for the selected metric.
        </p>
        <p className="section-header-desc mt-2">
          <strong>Note: </strong>
          <Button
            variant="link"
            className="p-0 align-baseline"
            onClick={() => setShowTutorial(true)}
          >
            View Tutorial
          </Button>
        </p>
      </header>

      <Modal
        show={showTutorial}
        onHide={handleCloseTutorial}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Sales Velocity Tutorial</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <video
            ref={tutorialVideoRef}
            controls
            autoPlay
            style={{ width: '100%' }}
          >
            <source src={TUTORIAL_VIDEO_SOURCE} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </Modal.Body>
      </Modal>

      <Card body className="card--xs card-with-border sales-velocity-controls">
        <Stack
          direction="horizontal"
          className="flex-wrap align-items-center gap-3"
        >
          <div className="sales-velocity-control">
            <Form.Label className="mb-0">Venue</Form.Label>
            <Form.Select
              value={venueUUID}
              onChange={(e) => setVenueUUID(e.target.value)}
              disabled={isLoadingVenues || venues.length === 0}
            >
              <option value="">Select a venue</option>
              {venueOptions}
            </Form.Select>
          </div>
          <div className="sales-velocity-control">
            <Form.Label className="mb-0">Heatmap metric</Form.Label>
            <Form.Select
              value={metricId}
              onChange={(e) => setMetricId(e.target.value)}
              disabled={!venueUUID || isLoadingHeatmap}
            >
              {metricOptions}
            </Form.Select>
          </div>
          {heatmapData?.updatedAt && (
            <p className="sales-velocity-updated small text-muted mb-0 ms-md-auto">
              Updated{' '}
              {new Date(heatmapData.updatedAt).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
          )}
          <ReportRefreshButton
            onRefresh={refresh}
            isRefreshing={isLoadingHeatmap}
            disabled={!venueUUID || isLoadingHeatmap}
          />
        </Stack>
        {selectedMetric?.description && (
          <p className="sales-velocity-metric-desc small text-muted mb-0">
            {selectedMetric.description}
          </p>
        )}
      </Card>

      {loadError && (
        <Card body className="card--xs card-with-border mt-3">
          <p className="text-danger mb-0">{loadError}</p>
        </Card>
      )}

      {isLoading && venueUUID && <PageLoadingContainer />}

      {!isLoading && venueUUID && renderEmptyState()}

      {!isLoading &&
        venueUUID &&
        heatmapData?.seatmap?.mapping &&
        heatmapData.seatCount > 0 && (
          <Row className="g-3 mt-1">
            <Col xl={8}>
              <Card
                body
                className="card--xs card-with-border sales-velocity-map-card"
              >
                <Stack
                  direction="horizontal"
                  className="justify-content-between align-items-center mb-3"
                >
                  <h2 className="h5 mb-0">
                    {heatmapData.venue?.name} — {selectedMetric?.label}
                  </h2>
                  <span className="text-muted small">
                    {heatmapData.seatCount.toLocaleString()} seats with data
                  </span>
                </Stack>
                <SalesVelocityHeatmapMap
                  mapping={heatmapData.seatmap.mapping}
                  background={heatmapData.seatmap.background}
                  seatBorderRadius={heatmapData.seatmap.seatBorderRadius}
                  venue={heatmapData.venue}
                  bySeatKey={heatmapData.bySeatKey}
                  metricId={metricId}
                  metricRanges={heatmapData.metricRanges}
                />
                <div className="sales-velocity-gradient-legend" aria-hidden>
                  <span>Low</span>
                  <div className="sales-velocity-gradient-bar" />
                  <span>High</span>
                </div>
              </Card>
            </Col>
            <Col xl={4}>
              <Card className="card--xs card-with-border sales-velocity-insights-card">
                <div className="sales-velocity-insights-header">
                  <Stack
                    direction="horizontal"
                    className="justify-content-between align-items-center"
                  >
                    <h2 className="h5 mb-0">Insights</h2>
                    <button
                      type="button"
                      className="btn btn-link p-0 sales-velocity-guide-link"
                      onClick={() => setShowMetricsGuide(true)}
                    >
                      Guide
                    </button>
                  </Stack>
                </div>
                <Card.Body className="sales-velocity-insights-scroll">
                  <InsightList
                    title="Sections that sell first"
                    items={insights?.fastestSections}
                    emptyMessage="Not enough section data yet."
                    renderItem={(item) => (
                      <>
                        <strong>Sec {item.section}</strong> — {item.sellThrough}
                        % sell-through
                      </>
                    )}
                  />
                  <InsightList
                    title="Rows hardest to move"
                    items={insights?.slowestRows}
                    emptyMessage="No consistently slow rows detected."
                    renderItem={(item) => (
                      <>
                        <strong>
                          Sec {item.section} Row {item.row}
                        </strong>{' '}
                        — {item.sellThrough}% sell-through
                      </>
                    )}
                  />
                  <InsightList
                    title="Premium inventory (revenue)"
                    items={insights?.premiumSections}
                    emptyMessage="No revenue concentration yet."
                    renderItem={(item) => (
                      <>
                        <strong>Sec {item.section}</strong> —{' '}
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          maximumFractionDigits: 0,
                        }).format(item.grossRevenue)}
                      </>
                    )}
                  />
                  <InsightList
                    title="Dead zones"
                    items={insights?.deadZones}
                    emptyMessage="No dead zones under 25% sell-through."
                    renderItem={(item) => (
                      <>
                        Sec {item.section}
                        {item.row ? ` Row ${item.row}` : ''}
                        {item.seat ? ` Seat ${item.seat}` : ''} —{' '}
                        {item.sellThrough}%
                      </>
                    )}
                  />
                  <InsightList
                    title="Pricing opportunities"
                    items={insights?.pricingOpportunities}
                    emptyMessage="No underperforming premium seats flagged."
                    renderItem={(item) => (
                      <>
                        Sec {item.section}
                        {item.row ? ` Row ${item.row}` : ''} —{' '}
                        {item.sellThrough}% at{' '}
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        }).format(item.averageTicketPrice)}
                      </>
                    )}
                  />
                  <InsightList
                    title="Comp & promo impact"
                    items={insights?.compHeavyAreas}
                    emptyMessage="No high comp-ratio areas detected."
                    renderItem={(item) => (
                      <>
                        Sec {item.section}
                        {item.row ? ` Row ${item.row}` : ''} — {item.compRatio}%
                        comps ({item.ticketsSold} tickets)
                      </>
                    )}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

      {!venueUUID && !isLoadingVenues && (
        <Card body className="card--xs card-with-border mt-3">
          <p className="text-muted mb-0">
            Select a venue to view organization-wide seat location analytics on
            the seatmap.
          </p>
        </Card>
      )}

      <SalesVelocityMetricsGuideModal
        show={showMetricsGuide}
        onHide={() => setShowMetricsGuide(false)}
      />
    </section>
  )
}
